import { observer } from 'mobx-react-lite'
import { links } from '@/utils/link'
import { formatVisibility } from '@/features/jobs/details/amendments-card-formatters'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { CollaboratorsMenuContainer } from '@/features/jobs/details/components/CollaboratorsCard/CollaboratorsCardContainer'
import { type CollaboratorViewModel } from '@/features/jobs/details/components/CollaboratorsCard/CollaboratorViewModel'
import { RequestedProviderContainer } from '@/features/jobs/details/components/CollaboratorsCard/RequestedProvider/RequestedProviderContainer'
import { JobScopeChangeRequestsCardContainer } from '@/features/jobs/details/components/JobScopeChangeRequestsCard/JobScopeChangeRequestsCardContainer'
import { JobTicketsCardContainer } from '@/features/jobs/details/components/JobTicketsCard/JobTicketsCardContainer'
import { PaymentDistributionCardContainer } from '@/features/jobs/details/components/PaymentDistributionCard/PaymentDistributionCardContainer'
import { JobInsightsCardContainer } from '@/features/jobs/details/components/JobInsightsCard/JobInsightsCardContainer'
import { FinalReturnAmountsDialogContainer } from '@/features/jobs/details/components/FinalReturnAmountsDialog/FinalReturnAmountsDialogContainer'
import {
  JobDetailsRail,
  JobDetailsRailAdminGroup,
  JobDetailsRailAmendments,
  JobDetailsRailInfoLines,
  JobDetailsRailPeople,
  JobDetailsRailPerson,
  JobDetailsRailProperties,
  JobDetailsRailPropertiesMenu,
  JobDetailsRailReturnResults,
  JobDetailsRailReturnResultsPlaceholder,
  JobDetailsRailServices,
} from '@/features/jobs/details/components/JobDetailsRail'
import { ReturnResultsDialog } from '@/features/jobs/details/components/ReturnResultsDialog'
import {
  formatYearReturnAmountsSource,
  netAmount,
} from '@/features/jobs/details/components/FinalReturnAmountsCard/return-amounts'

export interface JobDetailsRailContainerProps {
  vm: JobDetailsViewModel
}

export const JobDetailsRailContainer = observer(
  function JobDetailsRailContainer({ vm }: JobDetailsRailContainerProps) {
    return (
      <JobDetailsRail moreCards={<MoreCardsContainer vm={vm} />}>
        <ReturnResultsContainer vm={vm} />
        <PropertiesContainer vm={vm} />
        {vm.job.infoLines.length > 0 && (
          <JobDetailsRailInfoLines
            lines={vm.job.infoLines.map((line) => ({
              title: line.title,
              content: line.content,
            }))}
          />
        )}
        <JobDetailsRailPeople menu={<CollaboratorsMenuContainer vm={vm} />}>
          {vm.collaboratorsVm.collaborators.map((collaborator) => (
            <PersonContainer
              key={collaborator.member.user.id}
              collaboratorVm={collaborator}
            />
          ))}
          {vm.collaboratorsVm.showingRequestedProvider && (
            <RequestedProviderContainer
              provider={vm.job.requestedPro!}
              vm={vm}
            />
          )}
        </JobDetailsRailPeople>
        <JobDetailsRailServices
          items={vm.scopeChangesVm.serviceItemListVm.serviceItems.map(
            (item) => ({
              name: item.name,
              quantity: item.quantity,
            }),
          )}
          onViewAll={vm.scopeChangesVm.serviceItemListVm.show}
        />
        {vm.actionsVm.canViewAmendments && (
          <JobDetailsRailAmendments
            amendments={vm.detailsVm.amendmentsTableVm.orderedAmendments.map(
              (amendment) => ({
                description: amendment.description,
                customerAmount: amendment.customerAmount,
                internalNote: amendment.internalNote,
                visibility: formatVisibility(amendment.visibility),
                providerAmount: amendment.providerAmount,
                dateCreated: amendment.dateCreated,
              }),
            )}
            canAdd={!vm.completed}
            onAdd={vm.actionsVm.createAmendmentVm.show}
          />
        )}
      </JobDetailsRail>
    )
  },
)

const ReturnResultsContainer = observer(function ReturnResultsContainer({
  vm,
}: JobDetailsRailContainerProps) {
  const results = vm.finalReturnAmountsVm

  if (!results.canView) {
    // Only tax prep jobs ever get results, so only they explain the gap.
    return vm.isProgressionFlowEnabled ? (
      <JobDetailsRailReturnResultsPlaceholder />
    ) : null
  }

  return (
    <JobDetailsRailReturnResults
      net={results.net}
      yearCount={results.years.length}
      manualCount={results.manualCount}
      onViewBreakdown={results.breakdownModal.show}
    >
      <ReturnResultsDialog
        years={results.years.map((year) => ({
          taxYear: year.taxYear,
          federalAmount: year.federalAmount,
          stateAmounts: year.stateAmounts.map((state) => ({
            state: state.state,
            amount: state.amount,
          })),
          netAmount: netAmount(year),
          sourceLabel: formatYearReturnAmountsSource(year),
        }))}
        states={results.states}
        net={results.net}
        showing={results.breakdownModal.showing}
        onClose={results.breakdownModal.close}
        onEdit={results.canEdit ? results.edit : undefined}
      />
      <FinalReturnAmountsDialogContainer
        vm={results.dialogVm}
        submitLabel="Save"
      />
    </JobDetailsRailReturnResults>
  )
})

const PropertiesContainer = observer(function PropertiesContainer({
  vm,
}: JobDetailsRailContainerProps) {
  const job = vm.job

  return (
    <JobDetailsRailProperties
      menu={
        <JobDetailsRailPropertiesMenu
          canRename={vm.actionsVm.canRename}
          canUpdateDeadline={vm.actionsVm.canUpdateDeadline}
          canUpdateJobScope={vm.scopeChangesVm.canUpdateJobScope}
          onRename={vm.actionsVm.renameVm.show}
          onUpdateDeadline={vm.actionsVm.updateDeadlineDialogVm.modal.show}
          onUpdateJobScope={vm.scopeChangesVm.updateJobScopeVm.show}
        />
      }
      shortId={job.shortId}
      workspaceName={vm.jobWorkspace.name}
      showLegend={vm.canViewLegendNameAsAdmin}
      legendText={vm.canViewLegendNameAsAdmin ? vm.legendText : undefined}
      bundleName={job.jobBundle ? job.jobBundle.name : undefined}
      serviceItemCount={vm.scopeChangesVm.serviceItemListVm.serviceItems.length}
      providerCutAmount={
        vm.shouldShowProviderCutAmount
          ? vm.formattedProviderCutAmount
          : undefined
      }
      showTotal={vm.shouldShowJobTotal}
      total={job.total}
      payoutSplit={vm.billingVm.payoutSplitLabel}
      dateAccepted={job.dateAccepted}
      dateSubmitted={job.dateSubmitted}
      dateDeadline={job.dateDeadline}
      prepAndReview={
        job.containsPrepAndReviewLink
          ? {
              text: job.prepAndReviewJobText,
              linkType: job.prepAndReviewJobLinkType,
              onView: vm.viewPrepAndReviewJob,
            }
          : undefined
      }
      canSplitPayout={vm.billingVm.canSplitPayout}
      onCopyId={vm.copyShortId}
      onEditSplit={vm.billingVm.splitPayoutDialogVm.show}
    />
  )
})

interface PersonContainerProps {
  collaboratorVm: CollaboratorViewModel
}

const PersonContainer = observer(function PersonContainer({
  collaboratorVm,
}: PersonContainerProps) {
  const member = collaboratorVm.member

  return (
    <JobDetailsRailPerson
      avatar={{
        avatar: member.avatar,
        avatarFallback: member.avatarFallback,
        displayName: member.displayName,
      }}
      online={member.user.online}
      displayName={member.displayName}
      formattedType={collaboratorVm.formattedType}
      isCurrentUser={member.isCurrentUser}
      userDetailsLink={
        collaboratorVm.canViewUserDetails
          ? links.userDetails(member.user.publicId)
          : undefined
      }
      showCollaboratorInfo={collaboratorVm.canViewCollaboratorInfo}
      teamName={collaboratorVm.team ? collaboratorVm.team.name : undefined}
      email={collaboratorVm.canViewCollaboratorEmail ? member.email : undefined}
      phone={
        collaboratorVm.canViewCollaboratorPhoneNumber ? member.phone : undefined
      }
      formattedRole={collaboratorVm.formattedRole}
    />
  )
})

/**
 * Records only some jobs carry. Each card keeps its own title, so the rail
 * adds none.
 */
const MoreCardsContainer = observer(function MoreCardsContainer({
  vm,
}: JobDetailsRailContainerProps) {
  return (
    <>
      {vm.scopeChangesVm.hasVisibleResolvedRequests && (
        <JobScopeChangeRequestsCardContainer vm={vm} resolvedOnly />
      )}
      <JobTicketsCardContainer vm={vm} />
      {vm.hasAdminCards && (
        <JobDetailsRailAdminGroup>
          {vm.billingVm.canViewPaymentDistributionAsAdmin && (
            <PaymentDistributionCardContainer vm={vm} />
          )}
          {vm.canViewJobInsights && <JobInsightsCardContainer vm={vm} />}
        </JobDetailsRailAdminGroup>
      )}
    </>
  )
})
